import React, { createContext, useContext, useState, useEffect } from 'react';
import { isLabAdmin } from '../lib/utils';

const LabAuthContext = createContext();

/**
 * Provider component that checks lab admin permissions once on mount
 * and shares the result across all components
 */
export const LabAuthProvider = ({ children }) => {
    const [isLabAdminStatus, setIsLabAdminStatus] = useState(null); // null = not checked yet
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        const checkLabAccess = async () => {
            try {
                const result = await isLabAdmin();
                if (isMounted) {
                    setIsLabAdminStatus(result['status']);
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Failed to check lab admin permission:', error);
                if (isMounted) {
                    setIsLabAdminStatus(false);
                    setIsLoading(false);
                }
            }
        };

        // Start permission check in background (non-blocking)
        checkLabAccess();

        return () => {
            isMounted = false;
        };
    }, []);

    return (
        <LabAuthContext.Provider value={{ isLabAdmin: isLabAdminStatus, isLoading }}>
            {children}
        </LabAuthContext.Provider>
    );
};

/**
 * Hook to access lab admin permission status from any component
 * @returns {Object} { isLabAdmin: boolean|null, isLoading: boolean }
 */
export const useLabAuth = () => {
    const context = useContext(LabAuthContext);
    if (context === undefined) {
        throw new Error('useLabAuth must be used within a LabAuthProvider');
    }
    return context;
};
